import { useState, useMemo } from 'react'
import { Building2, Share2, Link2 } from 'lucide-react'
import { industries } from '../data/roles.ts'
import { roles } from '../data/roles.ts'
import SeoH1 from '../components/SeoH1.tsx'

const availableCalculators = [
  { id: 'ai-salary-gap', title: 'AI Salary Gap Calculator', desc: 'Show each employee their personal AI wage premium.' },
  { id: 'skills-roi', title: 'AI Skills ROI Calculator', desc: 'Rank the highest-ROI skills for their role.' },
  { id: 'displacement-risk', title: 'AI Displacement Risk Calculator', desc: 'Reframe risk in dollars and upskilling actions.' },
  { id: 'ai-productivity', title: 'AI Productivity Value Calculator', desc: 'Turn hours saved into annual dollar value.' },
  { id: 'employee-ai-reimbursement', title: 'AI Expense Reimbursement Calculator', desc: 'Build the case for employer-paid AI tools.' },
  { id: 'lifetime-earnings', title: 'Lifetime Earnings Calculator', desc: 'Career NPV with and without AI upskilling.' }
]

export default function EnterpriseReportBuilder() {
  const [companyName, setCompanyName] = useState('')
  const [teamSize, setTeamSize] = useState(50)
  const [industryId, setIndustryId] = useState(industries[0].id)
  const [roleId, setRoleId] = useState(roles[0].id)
  const [message, setMessage] = useState('')
  const [selectedCalculators, setSelectedCalculators] = useState<string[]>(['ai-salary-gap', 'skills-roi', 'displacement-risk'])
  const [copied, setCopied] = useState(false)

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (companyName) params.set('company', companyName)
    params.set('size', String(teamSize))
    params.set('industry', industryId)
    params.set('role', roleId)
    if (message) params.set('message', message)
    params.set('calcs', selectedCalculators.join(','))
    return `${window.location.origin}/enterprise-report?${params.toString()}`
  }, [companyName, teamSize, industryId, roleId, message, selectedCalculators])

  const toggleCalculator = (id: string) => {
    setSelectedCalculators(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    )
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Building2 className="w-6 h-6" />
          </div>
          <SeoH1 fallback="Enterprise AI Readiness Report Builder" />
        </div>
        <p className="text-slate-600 max-w-2xl">
          Build a white-labeled AI readiness report for your team. Share a single link to Slack, email, or your intranet.
        </p>
      </div>

      <div className="card p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Company name</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Corp"
            className="w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team size</label>
            <input
              type="number"
              value={teamSize}
              onChange={(e) => setTeamSize(Number(e.target.value))}
              min={1}
              max={100000}
              className="w-full rounded-md border-slate-300 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
            <select
              value={industryId}
              onChange={(e) => setIndustryId(e.target.value)}
              className="w-full rounded-md border-slate-300 text-sm"
            >
              {industries.map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Primary role (optional)</label>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full rounded-md border-slate-300 text-sm"
          >
            {roles.map(r => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Custom message to your team</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="We're investing in AI literacy. Use these calculators to see your personal AI wage gap, displacement risk, and the highest-ROI skills to learn."
            rows={4}
            className="w-full rounded-md border-slate-300 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Calculators to include</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableCalculators.map(calc => (
              <label
                key={calc.id}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCalculators.includes(calc.id) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedCalculators.includes(calc.id)}
                  onChange={() => toggleCalculator(calc.id)}
                  className="mt-1 rounded border-slate-300"
                />
                <div>
                  <div className="text-sm font-medium text-slate-900">{calc.title}</div>
                  <div className="text-xs text-slate-500">{calc.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCopy}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Link2 className="w-4 h-4" />
            {copied ? 'Link copied' : 'Copy shareable link'}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Preview report
          </a>
        </div>

        <p className="text-sm text-slate-500">
          The report is client-side and does not store any data. Anyone with the link can see the white-labeled report and use the calculators anonymously.
        </p>
      </div>
    </div>
  )
}
